import { useCallback, useEffect, useRef, useState } from 'react';
import axiosInstance from '../../utils/axiosConfig';
import { useSocket } from '../../context/SocketContext';

// Sin socket (se cayó o no conectó) se pregunta cada tanto; con socket llega todo al momento
const RESPALDO_MS = 20000;
const POR_SI_ACASO_MS = 120000;
const ULTIMOS_POR_PUERTA = 6;

const masNuevoPrimero = (a, b) => (a.fecha_hora < b.fecha_hora ? 1 : a.fecha_hora > b.fecha_hora ? -1 : b.id - a.id);

/**
 * Las puertas con su estado y lo último que pasó en cada una, en vivo. El vigía del backend
 * copia la bitácora de GeoVision y empuja por Socket.IO cada pase y cada cambio de una
 * puerta a la sala de Puertas; esta pantalla entra a esa sala mientras está abierta.
 *
 * suscribir(fn) avisa de cada evento nuevo (las tarjetas lo usan para animar la puerta).
 */
export const useEnVivo = () => {
    const { socket, connected } = useSocket();
    const [estado, setEstado] = useState(null);   // { puertas, ultimos, puede_operar, puede_gestionar }
    const [error, setError] = useState(null);
    const oyentes = useRef(new Set());
    const puedeOperar = useRef(false);

    const cargar = useCallback(async () => {
        try {
            const { data } = await axiosInstance.get('/api/puertas/estado');
            puedeOperar.current = data.puede_operar;
            setEstado(data);
            setError(null);
        } catch (e) {
            setError(e.response?.data?.error || 'No se pudo consultar el control de puertas');
        }
    }, []);

    useEffect(() => { cargar(); }, [cargar]);

    // Socket: entrar a la sala (también al reconectar) y escuchar
    useEffect(() => {
        if (!socket) return undefined;
        const unirse = () => socket.emit('puertas_unirse');
        const alEventos = (data) => {
            const nuevos = data?.eventos || [];
            if (!nuevos.length) return;
            setEstado((prev) => {
                if (!prev) return prev;
                const ultimos = { ...prev.ultimos };
                nuevos.forEach((ev) => {
                    if (ev.puerta_id == null || !(ev.puerta_id in ultimos)) return;
                    const lista = ultimos[ev.puerta_id] || [];
                    if (lista.some((x) => x.id === ev.id)) return;
                    ultimos[ev.puerta_id] = [ev, ...lista].sort(masNuevoPrimero).slice(0, ULTIMOS_POR_PUERTA);
                });
                return { ...prev, ultimos };
            });
            nuevos.forEach((ev) => oyentes.current.forEach((fn) => fn(ev)));
        };
        const alEstado = (data) => {
            if (!data?.puertas) return;
            // El estado que empuja el vigía es el mismo para todos: las acciones quedan
            // solo para quien puede operar
            const puertas = puedeOperar.current ? data.puertas : data.puertas.map((p) => ({ ...p, acciones: [] }));
            setEstado((prev) => (prev ? { ...prev, puertas } : prev));
        };
        if (socket.connected) unirse();
        socket.on('connect', unirse);
        socket.on('puertas_eventos', alEventos);
        socket.on('puertas_estado', alEstado);
        return () => {
            socket.emit('puertas_salir');
            socket.off('connect', unirse);
            socket.off('puertas_eventos', alEventos);
            socket.off('puertas_estado', alEstado);
        };
    }, [socket]);

    // Sin socket se pregunta cada 20 s; con socket, igual cada 2 min por si se perdió algo
    useEffect(() => {
        const t = setInterval(() => document.visibilityState === 'visible' && cargar(), connected ? POR_SI_ACASO_MS : RESPALDO_MS);
        return () => clearInterval(t);
    }, [connected, cargar]);

    const suscribir = useCallback((fn) => {
        oyentes.current.add(fn);
        return () => oyentes.current.delete(fn);
    }, []);

    return { estado, error, enVivo: connected, recargar: cargar, suscribir };
};
